/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { IoCloseSharp } from 'react-icons/io5';
import Holon from '../Buttons/holon';
import Dropdown from '../Buttons/dropdown';
import { dateRange } from '../../utils/dateHelpers';
import ShortNameField from '../Common/shortNameField';
import {
  startSprint,
  validateEndDateOfSprint
} from '../../utils/sprintHelpers';
import DateRange from '../Common/dateRange';
import { CadenceStatuses } from '../../constants/storyStatusTypes';
import { allows } from '../../utils/authorization';
import { truncate } from '../../utils/string';

class CadenceDetailsHeader extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editName: !props.cadence.id
    };
  }

  validStartDate = (currentDate, selectedDate) => {
    const { allCadences, cadence } = this.props;
    if (allCadences.length === 0) return true; // this is the first cadence
    const index = allCadences.findIndex((c) => c.id === cadence.id);
    let prevEndDate;
    if (index > 0) prevEndDate = allCadences[index - 1].end_date;
    else prevEndDate = allCadences[allCadences.length - 1].end_date;

    if ((selectedDate || currentDate).isSameOrBefore(cadence.end_date)) {
      return (selectedDate || currentDate).isSameOrAfter(prevEndDate);
    }
    return false;
  };

  validEndDate = (currentDate, selectedDate) => {
    const { cadence } = this.props;
    return !(selectedDate || currentDate).isBefore(cadence.start_date);
  };

  deleteCadence = () => {
    const { cadence } = this.props;
    this.props.handleDelete(cadence.id);
    this.props.onClose();
  };

  restoreCadence = () => {
    const { cadence } = this.props;
    this.props.handleRestore(cadence.id);
    this.props.onClose();
  };

  formatDate = (date) => {
    let splitDate;

    if (!date) return '';
    if (date._isAMomentObject) {
      const dateObject = date._d;
      return `${
        dateObject.getMonth() + 1
      }/${dateObject.getDate()}/${dateObject
        .getFullYear()
        .toString()
        .slice(2)}`;
    }
    if (date._i) splitDate = date._i.split('-');
    else splitDate = date.split('-');

    const year = splitDate.shift();
    splitDate.push(year.slice(2));
    return splitDate.join('/');
  };

  closeNameEdit = () => {
    this.setState({ editName: false });
  };

  openNameEdit = () => {
    if (this.state.editName) return;
    this.setState({ editName: true });
  };

  render() {
    const {
      cadence,
      allCadences,
      onClose,
      vrsTotal,
      handleUpdate,
      isValid,
      titleEdited,
      isStartable,
      isRestorable
    } = this.props;

    const { editName } = this.state;
    const hasSprintStarted =
      allCadences.findIndex((c) => c.status === CadenceStatuses.IN_PROGRESS) !==
      -1;
    const allowEdit =
      cadence.id > 0
        ? allows('update', cadence) &&
          cadence.status !== CadenceStatuses.ACCEPTED
        : true;
    const allowDelete =
      allows('destroy', cadence) &&
      cadence.status === CadenceStatuses.GETTING_READY;
    const allowRestore =
      allows('restore_cadences', cadence) &&
      isRestorable &&
      cadence.status === CadenceStatuses.GETTING_READY;
    const allowStartSprint =
      allows('update', cadence) &&
      isStartable &&
      cadence.status === CadenceStatuses.GETTING_READY &&
      !hasSprintStarted;
    const allowEndSprint =
      allows('update', cadence) &&
      cadence.status === CadenceStatuses.IN_PROGRESS;

    return (
      <div className="cadence-detail-header">
        <div className="dropdown-container">
          <Dropdown
            handleDelete={this.deleteCadence}
            handleRestore={this.restoreCadence}
            allowDelete={allowDelete}
            allowEdit={cadence.id ? allowEdit : false}
            allowRestore={allowRestore}
            allowStartSprint={allowStartSprint}
            allowEndSprint={allowEndSprint}
            startSprint={() => startSprint(cadence)}
            endSprint={() => validateEndDateOfSprint(cadence)}
            deleted={cadence.deleted_at}
          />
        </div>
        <div
          className="cadence-name-container"
          onClick={allowEdit ? this.openNameEdit : null}
        >
          {editName ? (
            <ShortNameField
              shortName={cadence.short_name}
              onNameChange={handleUpdate}
              allowEdit={allowEdit}
              titleEdited={titleEdited}
              isValid={isValid}
              unFocus={this.closeNameEdit}
              extraClasses="cadence-name"
              autoFocus
            />
          ) : (
            `${truncate(cadence.short_name, 60)}`
          )}
        </div>
        <div className="holon text-center">
          <Holon />
        </div>
        <div className="dates">
          {allowEdit ? (
            <DateRange
              startDate={this.formatDate(cadence.start_date)}
              endDate={this.formatDate(cadence.end_date)}
              onDateChange={handleUpdate}
              validStart={this.validStartDate}
              validEnd={this.validEndDate}
              isValid={isValid}
            />
          ) : (
            <strong>{dateRange(cadence)}</strong>
          )}
        </div>
        <div className="form-inline text-center">
          <div>
            <div className="vrs">
              <div name="value" className="value">
                Value - {vrsTotal.value}
              </div>
              <div name="risk" className="risk">
                Risk - {vrsTotal.risk}
              </div>
              <div name="size" className="points">
                Points - {vrsTotal.size}
              </div>
              {cadence.id ? (
                <div className="detail-close">
                  <button type="button" className="btn-close" onClick={onClose}>
                    <IoCloseSharp size="2em" />
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

CadenceDetailsHeader.propTypes = {
  handleUpdate: PropTypes.func,
  handleDelete: PropTypes.func,
  onClose: PropTypes.func,
  cadence: PropTypes.object,
  vrsTotal: PropTypes.object,
  allCadences: PropTypes.array
};

export default CadenceDetailsHeader;
